import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY

async function callEdge(payload) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/send-notifications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${SUPABASE_KEY}` },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(await res.text())
}

function HoursBar({ used, total }) {
  if (!total) return null
  const pct = Math.min(100, (used / total) * 100)
  const remaining = total - used
  const cls = remaining <= 1 ? 'danger' : remaining / total <= 0.25 ? 'warn' : 'good'
  return (
    <div>
      <div className="hours-bar-track">
        <div className={`hours-bar-fill ${cls}`} style={{ width: `${pct}%` }} />
      </div>
      <div style={{ fontSize: 11, color: cls === 'danger' ? 'var(--danger)' : cls === 'warn' ? 'var(--warning)' : 'var(--text-faint)', marginTop: 2 }}>
        {remaining.toFixed(1)} of {total} hrs remaining
      </div>
    </div>
  )
}

// ── Client Management Modal ──────────────────────────────────────────────────
function ClientModal({ client, onClose, onSaved }) {
  const editing = !!client?.id
  const [form, setForm] = useState({
    full_name: client?.full_name || '',
    company_name: client?.company_name || '',
    email: client?.email || '',
    account_type: client?.account_type || 'bucket',
    bucket_hours_total: client?.bucket_hours_total || 10,
    bucket_hours_used: client?.bucket_hours_used || 0,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  function set(k, v) { setForm(f => ({ ...f, [k]: v })) }

  async function submit(e) {
    e.preventDefault()
    setLoading(true); setError('')
    if (editing) {
      const { error } = await supabase.from('profiles').update({
        full_name: form.full_name,
        company_name: form.company_name,
        account_type: form.account_type,
        bucket_hours_total: parseFloat(form.bucket_hours_total) || 0,
        bucket_hours_used: parseFloat(form.bucket_hours_used) || 0,
      }).eq('id', client.id)
      if (error) { setError(error.message); setLoading(false); return }
    } else {
      // Add hours to existing account — look up by email
      const { data: existing } = await supabase.from('profiles').select('id').eq('email', form.email).single()
      if (existing) {
        const { error } = await supabase.from('profiles').update({
          full_name: form.full_name,
          company_name: form.company_name,
          account_type: form.account_type,
          bucket_hours_total: parseFloat(form.bucket_hours_total) || 0,
        }).eq('id', existing.id)
        if (error) { setError(error.message); setLoading(false); return }
      } else {
        setError('No account found with that email. The client must sign up first.')
        setLoading(false); return
      }
    }
    onSaved(); onClose()
  }

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <h3 style={{ marginBottom: 20 }}>{editing ? 'Edit client' : 'Add / update client'}</h3>
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group">
              <label>Full name</label>
              <input type="text" value={form.full_name} onChange={e => set('full_name', e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Company name</label>
              <input type="text" value={form.company_name} onChange={e => set('company_name', e.target.value)} required />
            </div>
          </div>
          {!editing && (
            <div className="form-group">
              <label>Email address</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="Client must have signed up first" required />
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Account type</label>
              <select value={form.account_type} onChange={e => set('account_type', e.target.value)}>
                <option value="contracted">Contracted</option>
                <option value="bucket">Bucket hours</option>
              </select>
            </div>
            {form.account_type === 'bucket' && (
              <div className="form-group">
                <label>Total hours purchased</label>
                <input type="number" step="1" min="1" value={form.bucket_hours_total} onChange={e => set('bucket_hours_total', e.target.value)} />
              </div>
            )}
          </div>
          {editing && form.account_type === 'bucket' && (
            <div className="form-group">
              <label>Hours used so far</label>
              <input type="number" step="0.25" min="0" value={form.bucket_hours_used} onChange={e => set('bucket_hours_used', e.target.value)} />
            </div>
          )}
          <div className="flex gap-2" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : editing ? 'Save changes' : 'Update client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Log Time Modal ───────────────────────────────────────────────────────────
function LogTimeModal({ ticket, onClose, onSaved }) {
  const [hours, setHours] = useState('')
  const [notes, setNotes] = useState('')
  const [status, setStatus] = useState(ticket.status)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function submit(e) {
    e.preventDefault()
    if (!hours || isNaN(hours) || parseFloat(hours) <= 0) { setError('Enter a valid number of hours.'); return }
    setLoading(true); setError('')
    const h = parseFloat(hours)
    const prevStatus = ticket.status

    const { error: entryErr } = await supabase.from('time_entries').insert({
      ticket_id: ticket.id, client_id: ticket.client_id, hours: h, notes,
    })
    if (entryErr) { setError(entryErr.message); setLoading(false); return }

    await supabase.from('tickets').update({ status }).eq('id', ticket.id)

    if (ticket.profiles?.account_type === 'bucket') {
      const newUsed = (ticket.profiles.bucket_hours_used || 0) + h
      await supabase.from('profiles').update({ bucket_hours_used: newUsed }).eq('id', ticket.client_id)
    }

    // Send resolved email if status changed to resolved
    if (status === 'resolved' && prevStatus !== 'resolved') {
      try {
        await callEdge({
          type: 'ticket_resolved',
          ticket: { ...ticket, resolution_notes: notes },
          clientEmail: ticket.profiles?.email,
          clientName: ticket.profiles?.company_name,
        })
      } catch (e) { console.error('Email error:', e) }
    }

    onSaved(); onClose()
  }

  const isBucket = ticket.profiles?.account_type === 'bucket'
  const remaining = isBucket ? (ticket.profiles.bucket_hours_total - ticket.profiles.bucket_hours_used) : null

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <h3 style={{ marginBottom: 4 }}>Log time</h3>
        <p className="text-muted text-sm" style={{ marginBottom: 20 }}>#{ticket.id} — {ticket.subject} · {ticket.profiles?.company_name}</p>
        {isBucket && remaining !== null && (
          <div className={`alert ${remaining <= 1 ? 'alert-error' : remaining <= 2 ? 'alert-warning' : 'alert-success'}`} style={{ marginBottom: 16 }}>
            Bucket balance: <strong>{remaining.toFixed(1)} hrs</strong> remaining before this entry
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Hours spent</label>
            <input type="number" step="0.25" min="0.25" value={hours} onChange={e => setHours(e.target.value)} placeholder="e.g. 1.5" required />
          </div>
          <div className="form-group">
            <label>Work notes (visible to client)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Describe what was done..." />
          </div>
          <div className="form-group">
            <label>Update ticket status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          {status === 'resolved' && ticket.status !== 'resolved' && (
            <div className="alert alert-success" style={{ fontSize: 12 }}>
              Marking as resolved will send an email to {ticket.profiles?.email}
            </div>
          )}
          <div className="flex gap-2" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : 'Save entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Send Update Modal ────────────────────────────────────────────────────────
function UpdateModal({ ticket, onClose, onSent }) {
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function send() {
    if (!message.trim()) { setError('Please enter a message.'); return }
    setLoading(true); setError('')
    try {
      await callEdge({
        type: 'ticket_update',
        ticket,
        clientEmail: ticket.profiles?.email,
        clientName: ticket.profiles?.company_name,
        updateMessage: message,
      })
      onSent(); onClose()
    } catch (err) { setError(err.message) }
    setLoading(false)
  }

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <h3 style={{ marginBottom: 4 }}>Send update to client</h3>
        <p className="text-muted text-sm" style={{ marginBottom: 20 }}>#{ticket.id} — {ticket.profiles?.company_name}</p>
        {error && <div className="alert alert-error">{error}</div>}
        <div className="form-group">
          <label>Message to client</label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="e.g. We need you to restart your computer and let us know if the issue persists..." style={{ minHeight: 120 }} />
        </div>
        <div className="alert" style={{ background: 'var(--navy-faint)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text-muted)' }}>
          This will email {ticket.profiles?.email} asking them to log in and respond.
        </div>
        <div className="flex gap-2" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={loading}>
            {loading ? <span className="spinner" /> : 'Send update'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Refill Reminder Modal ────────────────────────────────────────────────────
function RemindModal({ client, onClose, onSent }) {
  const remaining = client.bucket_hours_total - client.bucket_hours_used
  const [message, setMessage] = useState(
    `Hi ${client.company_name} team,\n\nJust a heads-up — you have ${remaining.toFixed(1)} hours remaining in your support bucket.\n\nTo avoid any delays, you may want to purchase an additional block of hours. Reply to this email or give us a call and we'll get you set up right away.\n\nThanks,\nVET1 IT Support`
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function send() {
    setLoading(true); setError('')
    try {
      await callEdge({ type: 'refill_reminder', clientEmail: client.email, clientName: client.company_name, updateMessage: message })
      await supabase.from('reminders').insert({ client_id: client.id, message })
      onSent(); onClose()
    } catch (err) { setError(err.message) }
    setLoading(false)
  }

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <h3 style={{ marginBottom: 20 }}>Send refill reminder — {client.company_name}</h3>
        {error && <div className="alert alert-error">{error}</div>}
        <div className="form-group">
          <label>Message</label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} style={{ minHeight: 160 }} />
        </div>
        <div className="flex gap-2" style={{ justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={loading}>
            {loading ? <span className="spinner" /> : 'Send email'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main Dashboard ───────────────────────────────────────────────────────────
export default function AdminDashboard() {
  const [tickets, setTickets] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('tickets') // 'tickets' | 'clients'
  const [logTarget, setLogTarget] = useState(null)
  const [updateTarget, setUpdateTarget] = useState(null)
  const [remindTarget, setRemindTarget] = useState(null)
  const [clientTarget, setClientTarget] = useState(null)
  const [showNewClient, setShowNewClient] = useState(false)
  const [filter, setFilter] = useState('all')

  async function load() {
    const [{ data: tix }, { data: cls }] = await Promise.all([
      supabase.from('tickets').select('*, profiles(*)').order('created_at', { ascending: false }),
      supabase.from('profiles').select('*').eq('is_admin', false).order('company_name'),
    ])
    setTickets(tix || [])
    setClients(cls || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const filtered = filter === 'all' ? tickets : tickets.filter(t => t.status === filter)
  const open = tickets.filter(t => t.status === 'open').length
  const inProg = tickets.filter(t => t.status === 'in_progress').length
  const resolved = tickets.filter(t => t.status === 'resolved').length
  const bucketClients = clients.filter(c => c.account_type === 'bucket')
  const needRemind = bucketClients.filter(c => (c.bucket_hours_total - c.bucket_hours_used) <= 2).length

  const statusBadge = { open: 'badge-open', in_progress: 'badge-progress', resolved: 'badge-resolved' }
  const statusLabel = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved' }
  const priorityBadge = { low: 'badge-low', medium: 'badge-medium', high: 'badge-high' }

  return (
    <div>
      <div className="stat-grid">
        <div className="stat-card"><div className="stat-label">Open</div><div className="stat-value">{open}</div></div>
        <div className="stat-card"><div className="stat-label">In progress</div><div className="stat-value">{inProg}</div></div>
        <div className="stat-card"><div className="stat-label">Resolved</div><div className="stat-value">{resolved}</div></div>
        <div className={`stat-card ${needRemind > 0 ? 'danger' : ''}`}>
          <div className="stat-label">Needs refill reminder</div>
          <div className="stat-value">{needRemind}</div>
          {needRemind > 0 && <div className="stat-sub text-danger">Low balance clients</div>}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        <button className={`btn ${tab === 'tickets' ? 'btn-primary' : ''}`} onClick={() => setTab('tickets')}>Tickets</button>
        <button className={`btn ${tab === 'clients' ? 'btn-primary' : ''}`} onClick={() => setTab('clients')}>
          Client management
        </button>
      </div>

      {/* ── TICKETS TAB ── */}
      {tab === 'tickets' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, alignItems: 'start' }}>
          <div className="card">
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h3>All tickets</h3>
              <div className="ml-auto flex gap-2">
                {['all','open','in_progress','resolved'].map(f => (
                  <button key={f} className={`btn btn-sm ${filter === f ? 'btn-primary' : ''}`} onClick={() => setFilter(f)}>
                    {f === 'all' ? 'All' : f === 'in_progress' ? 'In progress' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center' }}><span className="spinner" /></div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead><tr>
                    <th>#</th><th>Client</th><th>Subject</th><th>Type</th><th>Priority</th><th>Status</th><th>Date</th><th>Actions</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(t => (
                      <tr key={t.id}>
                        <td className="font-mono text-faint">#{t.id}</td>
                        <td style={{ fontWeight: 500 }}>{t.profiles?.company_name}</td>
                        <td>{t.subject}</td>
                        <td><span className={`badge ${t.profiles?.account_type === 'contracted' ? 'badge-contracted' : 'badge-bucket'}`}>
                          {t.profiles?.account_type === 'contracted' ? 'Contracted' : 'Bucket'}
                        </span></td>
                        <td><span className={`badge ${priorityBadge[t.priority]}`}>{t.priority}</span></td>
                        <td><span className={`badge ${statusBadge[t.status]}`}>{statusLabel[t.status]}</span></td>
                        <td className="text-faint">{new Date(t.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="flex gap-2">
                            <button className="btn btn-sm" onClick={() => setLogTarget(t)}>Log time</button>
                            <button className="btn btn-sm" onClick={() => setUpdateTarget(t)} title="Send update to client">Update</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-faint)', padding: 32 }}>No tickets</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Bucket sidebar */}
          <div>
            <h3 style={{ marginBottom: 12 }}>Bucket hour clients</h3>
            {bucketClients.length === 0 && <p className="text-faint text-sm">No bucket clients yet.</p>}
            {bucketClients.map(c => {
              const remaining = c.bucket_hours_total - c.bucket_hours_used
              const low = remaining <= 2
              return (
                <div key={c.id} className="card" style={{ padding: 14, marginBottom: 10, borderColor: low ? '#fca5a5' : undefined }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{c.company_name}</div>
                      <div className="text-faint text-xs">{c.email}</div>
                    </div>
                    {low && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--danger)', display: 'inline-block', marginTop: 4 }} />}
                  </div>
                  <HoursBar used={c.bucket_hours_used} total={c.bucket_hours_total} />
                  <div className="flex gap-2" style={{ marginTop: 10 }}>
                    <button className="btn btn-sm" style={{ flex: 1 }} onClick={() => setClientTarget(c)}>Edit hours</button>
                    {low && <button className="btn btn-sm" style={{ flex: 1 }} onClick={() => setRemindTarget(c)}>Remind</button>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── CLIENTS TAB ── */}
      {tab === 'clients' && (
        <div className="card">
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center' }}>
            <h3>All clients</h3>
            <button className="btn btn-primary btn-sm ml-auto" onClick={() => setShowNewClient(true)}>+ Add / update client</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr>
                <th>Company</th><th>Contact</th><th>Email</th><th>Type</th><th>Hours</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{c.company_name}</td>
                    <td className="text-muted">{c.full_name}</td>
                    <td className="text-muted">{c.email}</td>
                    <td><span className={`badge ${c.account_type === 'contracted' ? 'badge-contracted' : 'badge-bucket'}`}>
                      {c.account_type === 'contracted' ? 'Contracted' : 'Bucket'}
                    </span></td>
                    <td>
                      {c.account_type === 'bucket' ? (
                        <div style={{ minWidth: 120 }}>
                          <HoursBar used={c.bucket_hours_used} total={c.bucket_hours_total} />
                        </div>
                      ) : (
                        <span className="text-faint text-sm">Unlimited</span>
                      )}
                    </td>
                    <td>
                      <button className="btn btn-sm" onClick={() => setClientTarget(c)}>Edit</button>
                    </td>
                  </tr>
                ))}
                {clients.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-faint)', padding: 32 }}>No clients yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {logTarget && <LogTimeModal ticket={logTarget} onClose={() => setLogTarget(null)} onSaved={load} />}
      {updateTarget && <UpdateModal ticket={updateTarget} onClose={() => setUpdateTarget(null)} onSent={load} />}
      {remindTarget && <RemindModal client={remindTarget} onClose={() => setRemindTarget(null)} onSent={load} />}
      {clientTarget && <ClientModal client={clientTarget} onClose={() => setClientTarget(null)} onSaved={load} />}
      {showNewClient && <ClientModal onClose={() => setShowNewClient(false)} onSaved={load} />}
    </div>
  )
}
